/**
 * point-filtering.js
 */


// filters out points whose accuracy property is lower (better) than given value
export const filterAccuracy = (points, accuracy) => {
  const accuracyNum = +accuracy;
  return points.length <= 2
    ? points
    : points.filter(point => point.accuracy < accuracyNum);
}


// filters out points whose implied speed property is higher than given value
export const filterSpeedCalculated = (points, speed) => {
  const speedNum = +speed;
  return points.length <= 2
    ? points
    : points.filter(point => point.speedCalculated < speedNum);
}


// filters points whose accuracy deviates above moving avg + allowed deviation
export const filterAccuracyMovingAvg = (points, sampleSize, allowedAccuracyDev) => {
  if (points.length <= 2) {
    return points;
  }

  const allowedDevNum = +allowedAccuracyDev;
  const sampleSizeNum = +sampleSize;
  const outputPoints = [];

  // initialize avg to first point value
  let movingAvg = points[0].accuracy;

  points.forEach((point, i) => {
    movingAvg = movingAvg * ((sampleSizeNum - 1) / sampleSizeNum) + (point.accuracy / sampleSizeNum);
    if (point.accuracy < movingAvg + allowedDevNum) {
      outputPoints.push(point);
    }
  });

  return outputPoints;
}


// https://rosettacode.org/wiki/Ramer-Douglas-Peucker_line_simplification
export const RamerDouglasPeucker = (points, eps) => {
  if (points.length <= 2) {
    return points;
  }
    
  const last = points.length - 1;
  const p1 = points[0];
  const p2 = points[last];
  const x21 = p2.longitude - p1.longitude;
  const y21 = p2.latitude - p1.latitude;

  const [dMax, x] = points
      .slice(1, last)
      .map(p => Math.abs(y21 * p.longitude - x21 * p.latitude + p2.longitude * p1.latitude - p2.latitude * p1.longitude))
      .reduce((p, c, i) => {
        const v = Math.max(p[0], c);
        return [v, v === p[0] ? p[1] : i + 1];
      }, [-1, 0]);

  if (dMax > eps) {
    return [...RamerDouglasPeucker(points.slice(0, x + 1), eps), ...RamerDouglasPeucker(points.slice(x), eps).slice(1)];
  }

  return [points[0], points[last]];
}


// https://github.com/piercus/kalman-filter
export const Kalman = (points, algoId) => {
  if (points.length <= 2) {
    return points;
  }

  const { KalmanFilter } = require('kalman-filter');
  const algos = {
    '2ds': {
      observation: 2
    },
    '2dcp': {
      observation: {
        name: 'sensor',
        sensorDimension: 2,
      },
      dynamic: {
        name: 'constant-position',// observation.sensorDimension == dynamic.dimension
        covariance: [3, 4]// equivalent to diag([3, 4])
      }
    },
    '2dca': {
      observation: {
        name: 'sensor',
        sensorDimension: 2,
      },
      dynamic: {
        name: 'constant-acceleration', // observation.sensorDimension * 3 == state.dimension
        covariance: [3, 3, 4, 4, 5, 5], // equivalent to diag([3, 3, 4, 4, 5, 5])
        timeStep: 0.1,
      }
    }
  };

  const kFilter = new KalmanFilter(algos[algoId]);
  // const filteredPoints = kFilter.filterAll(points.slice(1, -1).map(point => [point.latitude, point.longitude]));
  // return [
  //   points[0],
  //   ...points
  //     .slice(1, -1)
  //     .map((point, i) => ({
  //       ...point,
  //       latitude: filteredPoints[i][0],
  //       longitude: filteredPoints[i][1]
  //     })),
  //   points[points.length - 1]
  // ];
  const filteredPoints = kFilter.filterAll(points.map(point => [point.latitude, point.longitude]));
  return points.map((point, i) => ({
    ...point,
    latitude: filteredPoints[i][0],
    longitude: filteredPoints[i][1]
  }));
}


// default point-filtering algo
export const defaultPointFilter = (points) => {
  if (!points) {
    return points;
  }

  // accuracy
  let newPoints = filterAccuracy(points, 50);
  
  // accuracy moving avg
  newPoints = filterAccuracyMovingAvg(newPoints, 10, 5);

  // ramer-douglas-peucker
  newPoints = RamerDouglasPeucker(newPoints, 1 / 1000000000);

  return newPoints;
}